import { Component, Inject, OnInit, NgZone, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { ShipmentAddressModel } from "@app/shared/CSDAModels/ShipmentAddressModel";
import { Countries } from "@app/shared/CSDAModels/Countries";
import { Provinces } from "@app/shared/CSDAModels/Provinces";
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { MatOptionModule } from "@angular/material/core";

import PlaceResult = google.maps.places.PlaceResult;
import AutocompleteOptions = google.maps.places.AutocompleteOptions;

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

interface changeCountryCallback { (form: FormGroup): void; }

@Component({
  selector: 'app-billing-address-dialog',
  standalone: true,
  templateUrl: './billing-address-dialog.component.html',
  styleUrls: ['./billing-address-dialog.component.scss'],
  imports: [
    NgxMaskDirective,
    ReactiveFormsModule,
    FormsModule,
    MatDialogModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule,
    BrowserAnimationsModule
  ],
  providers: [
    provideNgxMask()
  ]
})
export class BillingAddressDialogComponent implements OnInit {
  @ViewChild("billingSearchAddress") billingSearchAddress: ElementRef;
  @ViewChild("unit") UnitElement: ElementRef;

  billingForm: FormGroup;
  countriesList: Countries[] = [];
  statesProvincesList: Provinces[] = [];
  billingFilteredStateProvinces: Provinces[] = [];
  isQuickQuote = false;
  phoneMaskOrigin = '(000) 000-0000';
  autoCompleteOptions: AutocompleteOptions = {};
  appearance = 'outline';
  
  constructor(
    private translateService: TranslateService,
    private ngZone: NgZone,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<BillingAddressDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { address: ShipmentAddressModel }
  ) {}

  ngOnInit() {
    this.countriesList = JSON.parse(sessionStorage.getItem("Countries")) as Countries[] || [];
    this.statesProvincesList = JSON.parse(sessionStorage.getItem("Provinces")) as Provinces[] || [];

    this.billingForm = this.fb.group({
      companyName: [this.data?.address?.Name || '', [Validators.required]],
      personName: [this.data?.address?.AddressContacts?.[0]?.PersonName || '', [Validators.required, Validators.minLength(3)]],
      countryID: [
        this.data?.address?.CountryCode 
          ? (this.countriesList.find(c => c.CON_Code === this.data?.address?.CountryCode)?.CON_ID ?? '') 
          : '', 
        [Validators.required]
      ],
      phone: [this.data?.address?.AddressContacts?.[0]?.Phone || '', [Validators.required]],
      extension: [this.data?.address?.AddressContacts?.[0]?.Extension || ''],
      email: [this.data?.address?.AddressContacts?.[0]?.Email || '', [Validators.required, Validators.email]],
      isResidential: [this.data?.address?.IsResidential ?? true],
      streetName: [this.data?.address?.StreetName || '', [Validators.required]],
      unit: [this.data?.address?.Unit || ''],
      cityName: [this.data?.address?.CityName || '', [Validators.required]],
      zipCode: [this.data?.address?.ZipCode || '', [Validators.required]],
      stateID: [this.data?.address?.StateCode || '', [Validators.required]],
      specialInstruction: [this.data?.address?.SpecialInstructions || ''],
      taxID: [this.data?.address?.TaxID || '']
    });

    // Pre-filter if country exists on entry
    if (this.billingForm.controls['countryID'].value) {
      this.changeBillingCountryEvent();
    }
  }

  ngAfterViewInit() {
    setTimeout(() => this.initGoogleMapsAutocomplete(), 200); // Google API sometimes needs a delay
  }

  changeBillingCountryEvent(CallBack: changeCountryCallback = null): void {
    const Section: FormGroup = this.billingForm;
    const selectedCountry = this.countriesList.find(
      c => c.CON_ID == Section.controls['countryID'].value
    );

    if (selectedCountry) {
      if (selectedCountry.CON_Code == "CA" || selectedCountry.CON_Code == "US") {
        this.phoneMaskOrigin = "(000) 000-0000";
      } else {
        this.phoneMaskOrigin = "000000000000000";
      }

      const options: AutocompleteOptions = {
        types: ['address'],
        componentRestrictions: {country: selectedCountry.CON_Code},
      };
      
      this.autoCompleteOptions = Object.assign(this.autoCompleteOptions, options);
      this.initGoogleMapsAutocomplete();

      Section.controls["zipCode"].enable();
      Section.controls["stateID"].enable();

      if (selectedCountry.CON_ZipEmpty) {
        Section.controls["zipCode"].setValue(null);
        Section.controls["stateID"].setValue(null);
        Section.controls["zipCode"].disable();
        Section.controls["stateID"].disable();
      } else {
        if (selectedCountry.CON_StateMandatory) {
          this.billingFilteredStateProvinces = this.statesProvincesList.filter(
            f => f.PRV_CON_REF === Section.controls['countryID'].value
          );
          if (this.billingFilteredStateProvinces.length == 0)
            Section.controls["stateID"].disable();
        } else {
          Section.controls["stateID"].setValue(null);
          Section.controls["stateID"].disable();
        }
      }
    }
    if (CallBack) CallBack.call(Section);
  }

  initGoogleMapsAutocomplete() {
    if (!this.billingSearchAddress) return;
    const AddressSection: FormGroup = this.billingForm;
    const htmlInputElement: HTMLInputElement = this.billingSearchAddress.nativeElement;
    htmlInputElement.autocomplete = "off";
    htmlInputElement.value = "";

    google.maps.event.clearInstanceListeners(htmlInputElement);
    const autocomplete = new google.maps.places.Autocomplete(htmlInputElement, this.autoCompleteOptions);

    autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place: PlaceResult = autocomplete.getPlace();

        let streetNo: any, streetName: any, zipCode: any, municipality: any, cityName: any;
        let countryCode: any, countryName: any, stateCode: any, stateName: any;

        if (place.address_components) {
          place.address_components.forEach(value => {
            if (value.types.indexOf('street_number') > -1) streetNo = value.short_name;
            if (value.types.indexOf('route') > -1) streetName = value.long_name;
            if (value.types.indexOf('postal_code') > -1) zipCode = value.long_name;
            if (value.types.indexOf('sublocality') > -1) municipality = value.long_name;
            if (value.types.indexOf('locality') > -1) {
              cityName = (municipality && place.formatted_address.indexOf(municipality) != -1) ? municipality : value.long_name;
            }
            if (value.types.indexOf('administrative_area_level_1') > -1) {
              stateCode = value.short_name;
              stateName = value.long_name;
            }
            if (value.types.indexOf('country') > -1) {
              countryCode = value.short_name;
              countryName = value.long_name;
            }
          });

          const countryID = this.countriesList.find(a => a.CON_Code == countryCode)?.CON_ID;
          AddressSection.controls["streetName"].setValue([streetNo, streetName].filter(Boolean).join(' '));
          AddressSection.controls["zipCode"].setValue(zipCode);
          AddressSection.controls["cityName"].setValue(cityName);
          AddressSection.controls["countryID"].setValue(countryID);

          this.changeBillingCountryEvent();

          let stateID = -1;
          if (this.billingFilteredStateProvinces) {
            let province = this.billingFilteredStateProvinces.find(a => a.PRV_Code == stateCode)
              || this.billingFilteredStateProvinces.find(a => a.PRV_Name == stateName);
            if (province)
              stateID = province.PRV_ID;
          }
          AddressSection.controls["stateID"].setValue(stateID);
          AddressSection.controls["cityName"].setValue(cityName);

          this.billingSearchAddress.nativeElement.value = "";
          if (this.UnitElement) {
            this.UnitElement.nativeElement.focus();
          }
        }
      });
    });
  }

  saveBillingAddress() {
    const form = this.billingForm.value;
    const address: ShipmentAddressModel = {
      StreetName: form.streetName,
      CountryCode: this.countriesList.find(c => c.CON_ID === form.countryID)?.CON_Code || '',
      CountryName: this.countriesList.find(c => c.CON_ID === form.countryID)?.CON_Name || '',
      StateCode: this.statesProvincesList.find(s => s.PRV_ID === form.stateID)?.PRV_Code || '',
      StateName: this.statesProvincesList.find(s => s.PRV_ID === form.stateID)?.PRV_Name || '',
      CityName: form.cityName,
      ZipCode: form.zipCode,
      Valid: true,
      Corrected: false,
      IsEnglish: true,
      IsResidential: form.isResidential,
      Name: form.companyName,
      Unit: form.unit,
      SpecialInstructions: form.specialInstruction,
      TaxID: form.taxID,
      AddressContacts: [{
        PersonName: form.personName,
        CompanyName: form.companyName,
        Email: form.email,
        EmailNotification: false,
        Phone: form.phone,
        Extension: form.extension,
        Fax: null,
        Pager: null
      }]
    };

    this.dialogRef.close(address);
  }

  close() {
    this.dialogRef.close("cancel");
  }
}
